import { Vendor, VendorEscalation } from "../../types/vendors";

export const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Mirrors the backend defaults used when a lab has no settings. */
export const DEFAULT_CONFIRMATION_RULE: Record<Vendor["integration"], string> = {
  labstack: "60 minutes after the order reaches the lab",
  healthians: "60 minutes after the order reaches the lab",
  betacura: "24 hours before the appointment",
};

export interface EscalationForm {
  alertAmount: string;
  alertUnit: "minutes" | "hours";
  alertFrom: "" | "order" | "appointment";
  workingDays: number[];
  workingHoursStart: string;
  workingHoursEnd: string;
  holidays: string[];
}

export const emptyEscalationForm = (): EscalationForm => ({
  alertAmount: "", alertUnit: "minutes", alertFrom: "", workingDays: [], workingHoursStart: "", workingHoursEnd: "", holidays: [],
});

export function escalationFormFrom(value?: VendorEscalation | null): EscalationForm {
  const minutes = value?.confirmationAlertMinutes;
  const inHours = typeof minutes === "number" && minutes >= 60 && minutes % 60 === 0;
  return {
    alertAmount: typeof minutes === "number" ? String(inHours ? minutes / 60 : minutes) : "",
    alertUnit: inHours ? "hours" : "minutes",
    alertFrom: value?.confirmationAlertFrom || "",
    workingDays: [...(value?.workingDays || [])].sort(),
    workingHoursStart: value?.workingHoursStart || "",
    workingHoursEnd: value?.workingHoursEnd || "",
    holidays: [...(value?.holidays || [])].sort(),
  };
}

/** Returns an error message, or the payload (null clears the lab's settings). */
export function toEscalationPayload(form: EscalationForm): { error: string } | { value: VendorEscalation | null } {
  const amount = form.alertAmount.trim();
  const minutes = amount ? Number(amount) * (form.alertUnit === "hours" ? 60 : 1) : null;
  if (minutes !== null && (!Number.isInteger(minutes) || minutes < 1 || minutes > 48 * 60)) {
    return { error: "Enter a whole number of minutes or hours, up to 48 hours, for the confirmation alert." };
  }
  if ((minutes === null) !== !form.alertFrom) {
    return { error: "Set both the confirmation alert time and what it counts from, or leave both blank." };
  }
  if (Boolean(form.workingHoursStart) !== Boolean(form.workingHoursEnd) ||
    (form.workingHoursStart && form.workingHoursStart === form.workingHoursEnd)) {
    return { error: "Set both working-hours start and end, and make them different." };
  }
  const value: VendorEscalation = {
    confirmationAlertMinutes: minutes,
    confirmationAlertFrom: form.alertFrom || null,
    workingDays: [...form.workingDays].sort(),
    workingHoursStart: form.workingHoursStart || null,
    workingHoursEnd: form.workingHoursEnd || null,
    holidays: [...form.holidays].sort(),
  };
  const empty = minutes === null && !form.alertFrom && !value.workingDays!.length && !value.workingHoursStart && !value.holidays!.length;
  return { value: empty ? null : value };
}
